import { useState, useCallback, useRef, useEffect } from 'react';
import { FeatureCollection } from '../types/app.types';

interface DrawnLayersHookProps {
  sendMessage: (message: any) => void;
}

export const useDrawnLayers = ({ sendMessage }: DrawnLayersHookProps) => {
  const [drawnLayers, setDrawnLayers] = useState<FeatureCollection>({ 
    type: "FeatureCollection", 
    features: [] 
  });
  const [drawColor, setDrawColor] = useState("#eef205");

  // Simple debouncing with timeouts
  const updateTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const deleteTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());

  const onLayerEdited = useCallback((layerData: any) => {
    const layerId = layerData?.properties?.id;
    if (!layerId) {
      sendMessage({
        type: "update_layer",
        layer: layerData
      });
      return;
    }

    // Clear existing timeout for this layer
    const existingTimeout = updateTimeouts.current.get(layerId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new timeout
    const timeoutId = setTimeout(() => {
      sendMessage({
        type: "update_layer",
        layer: layerData
      });
      updateTimeouts.current.delete(layerId);
    }, 1000); // 1 second debounce

    updateTimeouts.current.set(layerId, timeoutId);
  }, [sendMessage]);

  const onLayerDeleted = useCallback((layerId: string) => {
    if (!layerId) return;

    // Clear any pending updates for this layer
    const updateTimeout = updateTimeouts.current.get(layerId);
    if (updateTimeout) {
      clearTimeout(updateTimeout);
      updateTimeouts.current.delete(layerId);
    }

    // Clear existing delete timeout
    const existingTimeout = deleteTimeouts.current.get(layerId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }

    // Set new timeout
    const timeoutId = setTimeout(() => {
      sendMessage({
        type: "delete_layer",
        layer_id: layerId
      });
      deleteTimeouts.current.delete(layerId);
    }, 500); // 500ms debounce for deletes

    deleteTimeouts.current.set(layerId, timeoutId);
  }, [sendMessage]);

  const handleLayerUpdate = useCallback((layer: any) => {
    if (layer && layer.properties && layer.properties.id) {
      const layerId = layer.properties.id;
      
      setDrawnLayers(prevCollection => {
        const filteredFeatures = prevCollection.features.filter(
          feature => feature.properties?.id !== layerId
        );
        return {
          type: "FeatureCollection",
          features: [...filteredFeatures, layer]
        };
      });
    }
  }, []);

  const handleLayerDelete = useCallback((layerId: string) => {
    setDrawnLayers(prevCollection => ({
      type: "FeatureCollection",
      features: prevCollection.features.filter(
        feature => feature.properties?.id !== layerId
      )
    }));
  }, []);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      updateTimeouts.current.forEach(timeout => clearTimeout(timeout));
      deleteTimeouts.current.forEach(timeout => clearTimeout(timeout));
      updateTimeouts.current.clear();
      deleteTimeouts.current.clear();
    };
  }, []);

  return {
    drawnLayers,
    drawColor,
    setDrawColor,
    onLayerEdited,
    onLayerDeleted,
    handleLayerUpdate,
    handleLayerDelete
  };
};